import React, { createContext, useState, useContext, FC, ReactNode, useEffect } from "react";
import { Header } from "build-ts/metadata";
import { getJobHeaders, getJob } from "../../services/BatchServices";
import { updatePlanHeader } from "../../services/AnalysisPlanServices";
import { JobProto, AnalysisActionRequest, JobRequest, ActionRequest, ActionRequest_ActionType, QnARequest } from "build-ts/jobs_interface";
import { createOrUpdateJob } from "../../services/BatchServices";
import showToast from "../../hooks/useCustomToast";
import { v4 as uuidv4 } from "uuid";

interface KGBatchContextProps {
  jobHeaders: Header[];
  jobLoaders: { headerLoader: boolean; actionLoader: boolean };
  selectedJobId: string | null;
  selectedJob: JobProto | null;
  selectedStepIndex: number | null;
  analysisActionRequests: AnalysisActionRequest[];
  actionRequestsByStepId: Map<string, ActionRequest[]>;
  jobRunHeaders: Header[];
  showStepsContainer: boolean;
  showStatusPage: boolean;
  selectedRunHeader: Header | null;
  setJobHeaders: React.Dispatch<React.SetStateAction<Header[]>>;
  setSelectedJobId: (id: string | null) => void;
  fetchJobDetails: (jobId: string) => Promise<void>;
  setSelectedJob: (job: JobProto | null) => void;
  setSelectedStepIndex: (index: number | null) => void;
  setAnalysisActionRequests: React.Dispatch<React.SetStateAction<AnalysisActionRequest[]>>;
  setActionRequestsByStepId: React.Dispatch<React.SetStateAction<Map<string, ActionRequest[]>>>;
  setJobRunHeaders: React.Dispatch<React.SetStateAction<Header[]>>;
  handleUpdateJob: (steps: AnalysisActionRequest[]) => Promise<void>;
  deleteStep: (index: number) => Promise<void>;
  duplicateStep: (index: number, duplicatePlanFn?: (planId: string) => Promise<Header | null>) => Promise<void>;
  duplicateJob: (job: JobProto, duplicatePlanFn?: (planId: string) => Promise<Header | null>) => Promise<Header | null>;
  renameStep: (index: number, newName: string) => Promise<void>;
  setShowStepsContainer: (show: boolean) => void;
  setShowStatusPage: (show: boolean) => void;
  setSelectedRunHeader: (header: Header | null) => void;
  toggleStepsContainer: () => void;
  updateStepWithPlanHeader: (stepIndex: number, planHeader: Header) => Promise<void>;
  createJobStepWithPlan: (planHeader: Header) => Promise<void>;
  addQnAAction: (stepId: string, questions: string[]) => Promise<void>;
  getQnAActionsForStep: (stepId: string) => ActionRequest[];
  updateQnAAction: (stepId: string, actionId: string, questions: string[]) => Promise<void>;
  deleteQnAAction: (stepId: string, actionId: string) => Promise<void>;
  fetchJobHeaders: () => Promise<void>;
}

// Create the context with a default value of undefined
const KGBatchContext = createContext<KGBatchContextProps | undefined>(undefined);

export const useKGBatchContext = () => {
  const context = useContext(KGBatchContext);
  if (!context) {
    throw new Error(
      "useKGBatchContext must be used within a KGBatchContextProvider"
    );
  }
  return context;
};

interface KGBatchContextProviderProps {
  children: ReactNode;
}

export const KGBatchContextProvider: FC<KGBatchContextProviderProps> = ({
  children,
}) => {
  const [jobHeaders, setJobHeaders] = useState<Header[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobProto | null>(null);
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);
  const [analysisActionRequests, setAnalysisActionRequests] = useState<AnalysisActionRequest[]>([]);
  const [actionRequestsByStepId, setActionRequestsByStepId] = useState<Map<string, ActionRequest[]>>(new Map());
  const [jobRunHeaders, setJobRunHeaders] = useState<Header[]>([]);
  const [showStepsContainer, setShowStepsContainer] = useState(false);
  const [showStatusPage, setShowStatusPage] = useState(false);
  const [selectedRunHeader, setSelectedRunHeader] = useState<Header | null>(null);
  const [jobLoaders, setJobLoaders] = useState<{ headerLoader: boolean; actionLoader: boolean }>({
    headerLoader: false,
    actionLoader: false,
  });

  // Update analysisActionRequests when selectedJob changes
  useEffect(() => {
    if (selectedJob?.request?.analysis_action_requests) {
      setAnalysisActionRequests(selectedJob.request.analysis_action_requests);
    } else {
      setAnalysisActionRequests([]);
    }
    if (selectedJob?.job_run_headers) {
      setJobRunHeaders(selectedJob?.job_run_headers);
    } else {
      setJobRunHeaders([]);
    }

    // Load existing actions and organize them by step ID
    if (selectedJob?.request?.actions) {
      const actionMap = new Map<string, ActionRequest[]>();
      selectedJob.request.actions.forEach(action => {
        if (action.parent_ids && action.parent_ids.length > 0) {
          const parentId = action.parent_ids[0]; // Use first parent ID as step ID
          const existingActions = actionMap.get(parentId) || [];
          actionMap.set(parentId, [...existingActions, action]);
        }
      });
      setActionRequestsByStepId(actionMap);
    } else {
      setActionRequestsByStepId(new Map());
    }
  }, [selectedJob]);

  // Function to fetch job headers
  const fetchJobHeaders = async () => {
    setJobLoaders({ ...jobLoaders, headerLoader: true });
    const headers = await getJobHeaders();
    if (headers) {
      setJobHeaders(headers);
    }
    setJobLoaders({ ...jobLoaders, headerLoader: false });
  };

  // Function to fetch job details
  const fetchJobDetails = async (jobId: string) => {
    setJobLoaders({ ...jobLoaders, actionLoader: true });
    const jobDetails = await getJob(jobId);
    if (jobDetails) {
      setSelectedJob(jobDetails);
    }
    setJobLoaders({ ...jobLoaders, actionLoader: false });
  };

  // Function to handle job updates
  const handleUpdateJob = async (steps: AnalysisActionRequest[]) => {
    if (!selectedJob?.header?.id) return;

    const jobRequest = JobRequest.create({
      global_properties: selectedJob.request?.global_properties,
      analysis_action_requests: steps,
      actions: selectedJob.request?.actions,
    });

    const response = await createOrUpdateJob({
      id: selectedJob.header.id,
      name: selectedJob.header.name,
      request: jobRequest,
    });

    if (response) {
      setSelectedJob(response);
      setAnalysisActionRequests(response.request?.analysis_action_requests || []);
      showToast("Job updated successfully", "", "success");
    }
  };

  // Function to update a step with a plan header
  const updateStepWithPlanHeader = async (stepIndex: number, planHeader: Header) => {
    if (stepIndex < 0 || stepIndex >= analysisActionRequests.length || !selectedJob?.header?.id) {
      showToast("Invalid step index or no job selected", "", "error");
      return;
    }
    // Update the step with the plan header
    const updatedSteps = [...analysisActionRequests];
    updatedSteps[stepIndex] = {
      ...updatedSteps[stepIndex],
      analysis_plan_header: planHeader,
    };
    // Update the job with the modified steps
    await handleUpdateJob(updatedSteps);
  };

  // Function to create a new job step with a plan
  const createJobStepWithPlan = async (planHeader: Header) => {
    if (!selectedJob?.header?.id) {
      showToast("No job selected", "", "error");
      return;
    }
    // Create a new analysis action request with the plan header
    const newStep = AnalysisActionRequest.create({
      step_id: uuidv4(),
      analysis_plan_header: planHeader,
    });
    // Add the new step to the existing steps
    const updatedSteps = [...analysisActionRequests, newStep];
    // Update the job with the new steps
    await handleUpdateJob(updatedSteps);
    // Set the newly created step as selected
    setSelectedStepIndex(updatedSteps.length - 1);
    showToast("New step created with plan", "", "success");
  };

  // Function to delete a step
  const deleteStep = async (index: number) => {
    const stepToDelete = analysisActionRequests[index];
    if (!stepToDelete) return;

    const updatedSteps = [...analysisActionRequests];
    updatedSteps.splice(index, 1);

    // Remove actions associated with the deleted step
    const newActionMap = new Map(actionRequestsByStepId);
    if (stepToDelete.step_id && newActionMap.has(stepToDelete.step_id)) {
      newActionMap.delete(stepToDelete.step_id);
    }

    // Update the job with both updated steps and actions
    await updateJobWithStepsAndActions(updatedSteps, newActionMap);

    // Update local state after successful job update
    setAnalysisActionRequests(updatedSteps);
    setActionRequestsByStepId(newActionMap);

    if (selectedStepIndex === index) {
      setSelectedStepIndex(null);
    }
  };

  // Function to duplicate a step
  const duplicateStep = async (index: number, duplicatePlanFn?: (planId: string) => Promise<Header | null>) => {
    const stepToDuplicate = analysisActionRequests[index];
    if (!stepToDuplicate) {
      showToast("Step not found", "", "error");
      return;
    }

    if (!stepToDuplicate.analysis_plan_header?.id) {
      showToast("Cannot duplicate step without associated plan", "", "error");
      return;
    }

    if (!duplicatePlanFn) {
      showToast("Duplicate plan function not available", "", "error");
      return;
    }

    const duplicatedPlan = await duplicatePlanFn(stepToDuplicate?.analysis_plan_header?.id);
    if (duplicatedPlan) {
      // Generate new step ID
      const newStepId = uuidv4();
      
      const duplicatedStep = AnalysisActionRequest.create({
        ...stepToDuplicate,
        step_id: newStepId,
        analysis_plan_header: duplicatedPlan,
      });

      // Duplicate actions associated with the original step
      const originalStepId = stepToDuplicate.step_id;
      const newActionMap = new Map(actionRequestsByStepId);
      
      if (originalStepId && actionRequestsByStepId.has(originalStepId)) {
        const originalActions = actionRequestsByStepId.get(originalStepId) || [];
        
        // Create duplicated actions with new IDs and updated parent_ids
        const duplicatedActions = originalActions.map(action => {
          return ActionRequest.create({
            ...action,
            step_id: uuidv4(), // Generate new action ID
            parent_ids: [newStepId], // Update parent_ids to reference the new step
          });
        });
        
        // Add duplicated actions to the action map
        if (duplicatedActions.length > 0) {
          newActionMap.set(newStepId, duplicatedActions);
        }
      }

      // Update the steps
      const updatedSteps = [...analysisActionRequests, duplicatedStep];
      
      // Update the job with both new steps and actions
      await updateJobWithStepsAndActions(updatedSteps, newActionMap);
      
      // Update local state after successful job update
      setAnalysisActionRequests(updatedSteps);
      setActionRequestsByStepId(newActionMap);
      setSelectedStepIndex(updatedSteps.length - 1);
      showToast("Step and associated actions duplicated successfully", "", "success");
    } else {
      showToast("Failed to duplicate plan", "", "error");
    }
  };

  // Function to duplicate a job
  const duplicateJob = async (job: JobProto, duplicatePlanFn?: (planId: string) => Promise<Header | null>): Promise<Header | null> => {
    if (!job) {
      showToast("No job selected", "", "error");
      return null;
    }
    if (!duplicatePlanFn) {
      showToast("Duplicate plan function not available", "", "error");
      return null;
    }

    try {
      const newJobHeader = Header.create({
        id: uuidv4(),
        name: job?.header?.name + " (Copy)",
      });

      const duplicatedSteps: AnalysisActionRequest[] = [];
      const duplicatedActions: ActionRequest[] = [];
      const stepIdMapping = new Map<string, string>(); // Map original step ID to new step ID

      // Duplicate all steps first
      for (const originalStep of job.request?.analysis_action_requests || []) {
        const newStepId = uuidv4();
        const originalStepId = originalStep.step_id;
        
        if (originalStepId) {
          stepIdMapping.set(originalStepId, newStepId);
        }

        let duplicatedPlan: Header | null = null;
        if (originalStep.analysis_plan_header?.id) {
          duplicatedPlan = await duplicatePlanFn(originalStep.analysis_plan_header.id);
          if (!duplicatedPlan) {
            showToast("Failed to duplicate plan for step " + originalStep.analysis_plan_header.name, "", "error");
            return null;
          }
        }

        const duplicatedStep = AnalysisActionRequest.create({
          ...originalStep,
          step_id: newStepId,
          analysis_plan_header: duplicatedPlan || undefined,
        });

        duplicatedSteps.push(duplicatedStep);
      }

      // Duplicate all actions associated with steps
      for (const originalAction of job.request?.actions || []) {
        if (originalAction.parent_ids && originalAction.parent_ids.length > 0) {
          const originalParentId = originalAction.parent_ids[0];
          const newParentId = stepIdMapping.get(originalParentId);
          
          if (newParentId) {
            const duplicatedAction = ActionRequest.create({
              ...originalAction,
              step_id: uuidv4(), // Generate new action ID
              parent_ids: [newParentId], // Update parent_ids to reference the new step
            });
            duplicatedActions.push(duplicatedAction);
          }
        }
      }

      // Create the new job request
      const newJobRequest = JobRequest.create({
        global_properties: job.request?.global_properties,
        analysis_action_requests: duplicatedSteps,
        actions: duplicatedActions,
      });

      // Create the new job
      const response = await createOrUpdateJob({
        name: newJobHeader.name,
        request: newJobRequest,
      });

      if (response && response.header) {
        // Update the job headers list with the new job
        setJobHeaders(prev => [...prev, response.header!]);
        showToast("Job duplicated successfully", "", "success");
        return response.header;
      } else {
        showToast("Failed to create duplicated job", "", "error");
        return null;
      }
    } catch (error) {
      showToast("Error duplicating job", "", "error");
      return null;
    }
  };

  // Function to rename a step
  const renameStep = async (index: number, newName: string) => {
    const stepToUpdate = analysisActionRequests[index];
    if (!stepToUpdate) return;
    if (stepToUpdate.analysis_plan_header?.id) {
      const response = await updatePlanHeader(stepToUpdate.analysis_plan_header?.id, newName);
      if (!response?.ok) {
        showToast("Error in renaming step", "", "error");
        return;
      }
    }
  };
  
  // Toggle steps container visibility
  const toggleStepsContainer = () => {
    setShowStepsContainer(!showStepsContainer);
    if (showStatusPage) {
      setShowStatusPage(false);
      setSelectedRunHeader(null);
    }
  };

  // Function to add (or remove) QnA action to a step
  const addQnAAction = async (stepId: string, questions: string[]) => {
    if (!selectedJob?.header?.id) {
      showToast("No job selected", "", "error");
      return;
    }

    // Clone the existing map so we can modify it immutably
    const newActionMap = new Map(actionRequestsByStepId);
    const currentActions = newActionMap.get(stepId) || [];

    // If the caller passed an empty questions array, remove any QNA actions
    if (questions.length === 0) {
      // Filter out QNA actions only
      const remaining = currentActions.filter(
        (action) => action.type !== ActionRequest_ActionType.QNA
      );

      if (remaining.length > 0) {
        newActionMap.set(stepId, remaining);
      } else {
        newActionMap.delete(stepId);
      }

      setActionRequestsByStepId(newActionMap);
      await updateJobWithActions(newActionMap);
      showToast("QnA action removed", "", "info");
      return;
    }

    // Otherwise, add (or replace) the QNA action
    const qnaAction = ActionRequest.create({
      step_id: uuidv4(),
      type: ActionRequest_ActionType.QNA,
      parent_ids: [stepId],
      qna_request: QnARequest.create({ questions: questions }),
    });

    // Drop any existing QNA actions first, so you don't end up with multiples for the same step:
    const nonQnA = currentActions.filter(
      (action) => action.type !== ActionRequest_ActionType.QNA
    );
    const updatedActions = [...nonQnA, qnaAction];

    newActionMap.set(stepId, updatedActions);
    setActionRequestsByStepId(newActionMap);
    await updateJobWithActions(newActionMap);

    showToast("QnA action added successfully", "", "success");
  };

  // Function to get QnA actions for a specific step
  const getQnAActionsForStep = (stepId: string): ActionRequest[] => {
    const actions = actionRequestsByStepId.get(stepId) || [];
    return actions.filter(action => action.type === ActionRequest_ActionType.QNA);
  };

  // Function to update QnA action
  const updateQnAAction = async (stepId: string, actionId: string, questions: string[]) => {
    if (!selectedJob?.header?.id) {
      showToast("No job selected", "", "error");
      return;
    }

    const currentActions = actionRequestsByStepId.get(stepId) || [];
    const updatedActions = currentActions.map(action => {
      if (action.step_id === actionId) {
        return {
          ...action,
          qna_request: QnARequest.create({
            questions: questions,
          }),
        };
      }
      return action;
    });

    const newActionMap = new Map(actionRequestsByStepId);
    newActionMap.set(stepId, updatedActions);
    setActionRequestsByStepId(newActionMap);

    // Update the job with the modified actions
    await updateJobWithActions(newActionMap);
    
    showToast("QnA action updated successfully", "", "success");
  };

  // Function to delete QnA action
  const deleteQnAAction = async (stepId: string, actionId: string) => {
    if (!selectedJob?.header?.id) {
      showToast("No job selected", "", "error");
      return;
    }

    const currentActions = actionRequestsByStepId.get(stepId) || [];
    const updatedActions = currentActions.filter(action => action.step_id !== actionId);

    const newActionMap = new Map(actionRequestsByStepId);
    if (updatedActions.length > 0) {
      newActionMap.set(stepId, updatedActions);
    } else {
      newActionMap.delete(stepId);
    }
    setActionRequestsByStepId(newActionMap);

    // Update the job with the modified actions
    await updateJobWithActions(newActionMap);
    
    showToast("QnA action deleted successfully", "", "success");
  };

  // Helper function to update job with actions
  const updateJobWithActions = async (actionMap: Map<string, ActionRequest[]>) => {
    if (!selectedJob?.header?.id) return;

    // Flatten all actions from the map
    const allActions: ActionRequest[] = [];
    actionMap.forEach(actions => {
      allActions.push(...actions);
    });

    const jobRequest = JobRequest.create({
      global_properties: selectedJob.request?.global_properties,
      analysis_action_requests: analysisActionRequests,
      actions: allActions,
    });

    const response = await createOrUpdateJob({
      id: selectedJob.header.id,
      name: selectedJob.header.name,
      request: jobRequest,
    });

    if (response) {
      setSelectedJob(response);
    }
  };

  // Helper function to update job with steps and actions
  const updateJobWithStepsAndActions = async (steps: AnalysisActionRequest[], actionMap: Map<string, ActionRequest[]>) => {
    if (!selectedJob?.header?.id) return;

    // Flatten all actions from the map
    const allActions: ActionRequest[] = [];
    actionMap.forEach(actions => {
      allActions.push(...actions);
    });

    const jobRequest = JobRequest.create({
      global_properties: selectedJob.request?.global_properties,
      analysis_action_requests: steps,
      actions: allActions,
    });

    const response = await createOrUpdateJob({
      id: selectedJob.header.id,
      name: selectedJob.header.name,
      request: jobRequest,
    });

    if (response) {
      setSelectedJob(response);
    }
  };
  
  return (
    <KGBatchContext.Provider
      value={{
        jobHeaders,
        jobLoaders,
        jobRunHeaders,
        selectedJobId,
        selectedJob,
        selectedStepIndex,
        analysisActionRequests,
        actionRequestsByStepId,
        showStepsContainer,
        showStatusPage,
        selectedRunHeader,
        setJobHeaders,
        setSelectedJobId,
        fetchJobDetails,
        setSelectedJob,
        setSelectedStepIndex,
        setAnalysisActionRequests,
        setActionRequestsByStepId,
        handleUpdateJob,
        deleteStep,
        duplicateStep,
        duplicateJob,
        renameStep,
        setJobRunHeaders,
        setShowStepsContainer,
        setShowStatusPage,
        setSelectedRunHeader,
        toggleStepsContainer,
        updateStepWithPlanHeader,
        createJobStepWithPlan,
        addQnAAction,
        getQnAActionsForStep,
        updateQnAAction,
        deleteQnAAction,
        fetchJobHeaders,
      }}
    >
      {children}
    </KGBatchContext.Provider>
  );
}; 